import React, { useEffect } from "react";
import { Button } from "react-bootstrap";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiLogoLinkedinSquare,
  BiSolidBookBookmark,
  BiSolidCalendarCheck,
  BiSolidNote,
  BiSupport
} from "react-icons/bi";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useGetTextScenarioStarterKitsQuery } from "../../app/services/chatbot-rtk.services";
import LoadingComponent from "../../components/common/loading/loading.component";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { handleResetAppearance } from "../appearance/appearanceSlice";
import {
  handleResetWizard,
  handleSetTemplateId,
  wizardState
} from "./botSlice";
import { ConnectForm } from "./BotWizard";
import { McBotFaceType } from "./types";

const TextScenarioTemplates = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const wizard = useAppSelector(wizardState);

  const { isLoading, isError, isSuccess, refetch, data } =
    useGetTextScenarioStarterKitsQuery(BotTypeEnum.TEXT_SCENARIO);

  const { setValue } = useFormContext();

  useEffect(() => {
    if (wizard.bot_config && (wizard.bot_config as McBotFaceType).template_id)
      setValue("template_id", (wizard.bot_config as McBotFaceType).template_id);
  }, [wizard.bot_config]);

  const renderIcon = {
    1: <BiSolidNote size={62} color="#FFFFFF" />,
    2: <BiSolidBookBookmark size={62} color="#FFFFFF" />,
    3: <BiSupport size={62} color="#FFFFFF" />,
    4: <BiSolidCalendarCheck size={62} color="#FFFFFF" />
  } as { [key: number]: React.ReactNode };

  useEffect(() => {
    if (
      !wizard.isInEditMode &&
      !(wizard.bot_config as McBotFaceType).template_id
    ) {
      dispatch(handleSetTemplateId(1));
      setValue("template_id", 1);
    }
  }, [wizard.isInEditMode, (wizard.bot_config as McBotFaceType).template_id]);

  let content: React.ReactNode;

  if (!isLoading) {
    content = (
      <div className="tw-px-8">
        <LoadingComponent />
      </div>
    );
  }

  if (isError) {
    content = (
      <div>
        {t("Wizard/general/key10")}
        <Button variant="link" onClick={refetch}>
          {t("Wizard/general/key11")}
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    content = (
      <ConnectForm>
        {({ setValue }: UseFormReturn<FieldValues, any>) => (
          <div className="md:tw-flex ">
            <div className="tw-grid  xxl:tw-grid-cols-5 xl:tw-grid-cols-4 lg:tw-grid-cols-3 md:tw-grid-cols-2 tw-grid-cols-1 tw-gap-4">
              {data.map((item: any, index: number) => (
                <div
                  key={index}
                  className={`tw-relative  tw-w-full tw-p-4 md:tw-h-full tw-bg-white tw-shrink tw-shadow tw-flex-col tw-justify-start tw-items-start tw-border-solid tw-rounded-lg md:tw-flex-1 ${
                    wizard.isInEditMode ? "tw-cursor-not-allowed" : ""
                  } ${
                    (wizard.bot_config as McBotFaceType).template_id === item.id
                      ? "tw-border-4 tw-border-blue-500"
                      : "tw-border-4 tw-border-transparent"
                  }  ${
                    wizard.isInEditMode &&
                    (wizard.bot_config as McBotFaceType).template_id !== item.id
                      ? "tw-hidden"
                      : "tw-inline-flex"
                  } ${wizard.isInEditMode ? "tw-cursor-default" : ""}`}
                  onClick={() => {
                    if (!wizard.isInEditMode) {
                      dispatch(handleSetTemplateId(item.id));
                      setValue("template_id", item.id);
                    }
                  }}
                  onKeyDown={() => {
                    if (!wizard.isInEditMode) {
                      dispatch(handleSetTemplateId(item.id));
                      setValue("template_id", item.id);
                    }
                  }}
                  role="button"
                  aria-label="bot-selector"
                  tabIndex={0}
                >
                  {wizard.isInEditMode &&
                    (wizard.bot_config as McBotFaceType).template_id !==
                      item.id && (
                      <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-bg-gray-200 tw-bg-opacity-50" />
                    )}
                  <img
                    src={item.icon}
                    alt="template-scenario"
                    className="tw-w-full tw-object-cover  tw-h-[120px] tw-self-stretch tw-bg-primary tw-rounded-lg tw-flex tw-flex-col tw-justify-center tw-items-center "
                  />
                  <div className=" tw-self-stretch  tw-pt-3 tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
                    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                      <div className=" tw-h-[80px] tw-w-full">
                        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                          <div className="tw-self-stretch tw-text-gray-900 text-base font-normal tw-font-['Lexend'] tw-leading-normal">
                            {item.title[i18n.language]}
                          </div>
                          <div className="tw-text-gray-400 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"></div>
                        </div>
                        <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend']  tw-mt-1">
                          {item.description[i18n.language]}
                        </div>
                      </div>
                      <div className="tw-h-[30px]  tw-flex tw-items-center tw-justify-between tw-w-full">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <img
                            src={item.publisher?.icon}
                            alt="template-scenario"
                            className="tw-w-7 tw-h-7 tw-rounded-full"
                          />
                          <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                            {item.publisher?.name}
                          </div>
                        </div>
                        {item.publisher?.socials && (
                          <>
                            {item.publisher?.socials?.linkedIn && (
                              <a
                                className="tw-cursor-pointer"
                                href={item.publisher?.socials?.linkedIn}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <BiLogoLinkedinSquare className="tw-w-4 tw-h-4 tw-text-gray-400" />
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* new component */}
              <div
                className={`tw-cursor-pointer tw-relative  tw-w-full tw-p-4 md:tw-h-full tw-bg-white tw-shrink tw-shadow tw-flex-col tw-justify-center tw-items-center tw-border-solid tw-rounded-lg md:tw-flex-1 ${
                  wizard.isInEditMode ? "tw-cursor-not-allowed" : ""
                } ${
                  (wizard.bot_config as McBotFaceType).template_id === 100
                    ? "tw-border-4 tw-border-blue-500"
                    : "tw-border-4 tw-border-transparent"
                }  ${
                  wizard.isInEditMode &&
                  (wizard.bot_config as McBotFaceType).template_id !== 100
                    ? "tw-hidden"
                    : "tw-inline-flex"
                } ${wizard.isInEditMode ? "tw-cursor-default" : ""}`}
              >
                <Link
                  onClick={() => {
                    dispatch(handleResetAppearance());
                    dispatch(handleResetWizard());
                  }}
                  to="contact"
                  className=" tw-p-4 tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-flex"
                >
                  <div className="tw-w-12 tw-h-12 tw-relative tw-bg-gray-200 tw-rounded-lg">
                    <div className=" tw-absolute " />
                    <div
                      data-svg-wrapper
                      className="tw-left-[11.66px] tw-top-[12px] tw-absolute"
                    >
                      <svg
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6641 2C7.15006 2 2.66406 6.486 2.66406 12C2.66406 17.514 7.15006 22 12.6641 22C18.1781 22 22.6641 17.514 22.6641 12C22.6641 6.486 18.1781 2 12.6641 2ZM17.6641 13H13.6641V17H11.6641V13H7.66406V11H11.6641V7H13.6641V11H17.6641V13Z"
                          fill="#312ee3"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className=" tw-flex-col tw-justify-center tw-gap-1 tw-flex">
                    <div className="  tw-text-center tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-whitespace-nowrap">
                      {t("TextScenario/key1")}
                    </div>
                    <div className="tw-text-center tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] ">
                      {t("TextScenario/key2")}
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </ConnectForm>
    );
  }

  return (
    <ConnectForm>
      {({ formState: { errors } }: UseFormReturn<FieldValues, any>) => (
        <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-gap-8 tw-inline-flex">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t("TextScenarioSetupChatbot/title")}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("TextScenarioSetupChatbot/description")}
              </div>
            </div>

            {errors.template_id && (
              <div className="tw-self-stretch tw-text-red-600 tw-text-xs tw-font-['Lexend'] tw-leading-none tw-block tw-mt-5">
                {errors?.template_id?.message as string}
              </div>
            )}

            {content}
          </div>
        </div>
      )}
    </ConnectForm>
  );
};

export default TextScenarioTemplates;
